import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export enum TableKeyEnum {
  PRODUCTION_CENTER = 'production_center',
  FEATURED_PEOPLE = 'featured_people',
  PARENT_CHANNEL = 'parent_channel',
  BROADCASTERS = 'BROADCASTERS',
  CHANNEL_RECORDINGS = 'channel_recordings',
  CHANNEL_RECORDING_TASKS = 'channel_recording_tasks',
  LOGOS = 'logos',
  SUPPORTS = 'supports',
  AUDIENCE_CHANNEL_SOURCES = 'audience_channel_sources',
  ECONOMIC_VALUE_CHANNEL_SOURCES = 'economic_value_channel_sources',
}

// Mirrors the options of the design-system paginator: a size it adds must be added here too,
// or it will be saved but fall back to the default on the next load.
const PAGE_SIZE_OPTIONS: readonly number[] = [10, 25, 50, 100];

export const DEFAULT_PAGE_SIZE = 10;

interface TableSettings {
  rowPerPage: number;
}

type TableSettingsValue = {
  [key in TableKeyEnum]?: TableSettings;
};

interface TableSettingsState {
  settings: TableSettingsValue;
  setRowsPerPage: (table: TableKeyEnum, value: number) => void;
  getRowsPerPage: (table: TableKeyEnum) => number;
}

export const useTableSettingsStore = create<TableSettingsState>()(
  persist(
    (set, get, api) => ({
      settings: {
        ...Object.fromEntries(
          Object.values(TableKeyEnum).map(value => [value, { rowPerPage: DEFAULT_PAGE_SIZE }])
        ),
      },
      setRowsPerPage: (table, value) => {
        // Must run before set(), never inside its updater: the updater would carry this tab's
        // stale settings and write them over what another tab saved in the meantime.
        api.persist.rehydrate();
        set(state => ({
          settings: {
            ...state.settings,
            [table]: {
              ...state.settings[table],
              rowPerPage: value,
            },
          },
        }));
      },
      getRowsPerPage: table => {
        const rowPerPage = get().settings[table]?.rowPerPage;
        return typeof rowPerPage === 'number' && PAGE_SIZE_OPTIONS.includes(rowPerPage)
          ? rowPerPage
          : DEFAULT_PAGE_SIZE;
      },
    }),
    {
      name: `media-manager-table-settings`,
    }
  )
);
